import { createMemo, createSignal, type Accessor } from 'solid-js';

export interface UseExpandedPropertySetsOptions {
  expandedRows: Accessor<readonly string[] | undefined>;
  onExpandedRowsChange(rows: string[]): void;
  regularSetKeys: Accessor<readonly string[]>;
  favoriteSetKeys: Accessor<readonly string[]>;
}

export interface UseExpandedPropertySetsResult {
  expandedKeys: Accessor<ReadonlySet<string>>;
  showAllInFavoriteSetKeys: Accessor<ReadonlySet<string>>;
  toggleKey(key: string): void;
  allExpanded: Accessor<boolean>;
  favoriteAllExpanded: Accessor<boolean>;
  toggleExpandCollapseAll(): void;
  toggleFavoriteExpandCollapseAll(): void;
  toggleShowAllForFavoriteSet(key: string): void;
}

/**
 * @intent Manages expand/collapse state for regular and favorite property
 * sets, including optional persisted expanded rows from the host.
 */
export function useExpandedPropertySets(
  options: UseExpandedPropertySetsOptions
): UseExpandedPropertySetsResult {
  const [internalExpandedKeys, setInternalExpandedKeys] = createSignal<
    ReadonlySet<string>
  >(new Set<string>());

  const expandedKeys = createMemo<ReadonlySet<string>>(() => {
    const persistedRows = options.expandedRows();
    if (persistedRows === undefined) return internalExpandedKeys();
    return new Set(persistedRows);
  });

  const updateExpandedKeys = (next: ReadonlySet<string>): void => {
    if (options.expandedRows() === undefined) {
      setInternalExpandedKeys(next);
    }
    options.onExpandedRowsChange([...next]);
  };

  const toggleKey = (key: string): void => {
    const next = new Set(expandedKeys());
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    updateExpandedKeys(next);
  };

  const updateExpandedSubset = (
    keys: readonly string[],
    shouldExpand: boolean
  ): void => {
    const next = new Set(expandedKeys());
    for (const key of keys) {
      if (shouldExpand) {
        next.add(key);
      } else {
        next.delete(key);
      }
    }
    updateExpandedKeys(next);
  };

  const allExpanded = createMemo((): boolean => {
    const keys = options.regularSetKeys();
    if (keys.length === 0) return false;
    const expanded = expandedKeys();
    return keys.every((key) => expanded.has(key));
  });

  const toggleExpandCollapseAll = (): void => {
    if (allExpanded()) {
      updateExpandedSubset(options.regularSetKeys(), false);
    } else {
      updateExpandedSubset(options.regularSetKeys(), true);
    }
  };

  const [showAllInFavoriteSetKeys, setShowAllInFavoriteSetKeys] = createSignal<
    ReadonlySet<string>
  >(new Set<string>());

  const favoriteAllExpanded = createMemo((): boolean => {
    const keys = options.favoriteSetKeys();
    if (keys.length === 0) return false;
    const expanded = expandedKeys();
    return keys.every((key) => expanded.has(key));
  });

  const toggleFavoriteExpandCollapseAll = (): void => {
    if (favoriteAllExpanded()) {
      updateExpandedSubset(options.favoriteSetKeys(), false);
    } else {
      updateExpandedSubset(options.favoriteSetKeys(), true);
    }
  };

  const toggleShowAllForFavoriteSet = (key: string): void => {
    setShowAllInFavoriteSetKeys((previousKeys) => {
      const nextKeys = new Set(previousKeys);
      if (nextKeys.has(key)) {
        nextKeys.delete(key);
      } else {
        nextKeys.add(key);
      }
      return nextKeys;
    });
  };

  return {
    expandedKeys,
    showAllInFavoriteSetKeys,
    toggleKey,
    allExpanded,
    favoriteAllExpanded,
    toggleExpandCollapseAll,
    toggleFavoriteExpandCollapseAll,
    toggleShowAllForFavoriteSet,
  };
}
